import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ReportBuilderService } from '@app/main/report/services/report-builder.service';
import { GuardService } from '@shared/services/guard.service';
import { ReportGetter } from '@app/main/report/directives/ReportGetter';
import { MatDialog } from '@angular/material/dialog';
import { FileService } from '@shared/services/file.service';
import { Router } from '@angular/router';
import { NavigationService } from '@shared/services/navigation.service';
import { DialogService, MailingService, SnackService } from '@shared/services';
import { ModuleService } from '@shared/services/module.service';
import { filter, Subject, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'app-report-standard',
    templateUrl: './report-standard.component.html',
    styleUrls: ['./report-standard.component.scss'],
    standalone: false
})
export class ReportStandardComponent extends ReportGetter implements OnDestroy {
    @Input() disableButtons: boolean;
    @Output() saveEmitter = new EventEmitter<string>();
    private destroy$ = new Subject<boolean>();

    constructor(
        private fileService: FileService,
        private snack: SnackService,
        private dialogService: DialogService,
        private mailingService: MailingService,
        guard: GuardService,
        builder: ReportBuilderService,
        dialog: MatDialog,
        router: Router,
        navigation: NavigationService,
        moduleService: ModuleService
    ) {
        super(builder, dialog, router, navigation, guard, moduleService);
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    get showButtons(): boolean {
        return window.innerWidth > 1190;
    }

    get innerWidth(): number {
        return window.innerWidth;
    }

    async export() {
        this.disableButtons = true;
        if (this.reportService.hasEmbeddedImages(this.report)) {
            this.dialogService
                .openDialog({
                    title: 'Export',
                    message: this.translateService.instant('report.actions.exportWithImage'),
                    confirmText: 'Continue'
                })
                .subscribe(async e => {
                    if (e && e.checked) {
                        try {
                            await this.fileService.exportReport(this.report);
                        } catch (error) {
                            this.snack.openSnackBarError('An error has occurred!');
                        }
                    }
                });
        } else {
            try {
                await this.fileService.exportReport(this.report);
            } catch (error) {
                this.snack.openSnackBarError('An error has occurred!');
            }
        }
        this.disableButtons = false;
    }

    async upload(file: File) {
        if (file) {
            if (file.type !== 'application/pdf') {
                this.snack.openSnackBarError('An error has occurred!');
                return;
            }

            this.dialogService
                .openDialog({
                    title: 'Upload',
                    message: `Are you sure you want to upload the following file?<br/><br/><b>${file.name}</b>`,
                    confirmText: 'Upload'
                })
                .subscribe(async e => {
                    if (e && e.checked) {
                        await this.reportService.uploadManualReport(this.report, file);
                        const report = await this.reportService.uploadManual(this.report);

                        this.snack.openSnackBarSuccess('Success');
                        this.goBack();
                    }
                });
        }
    }

    async sendMailToResponsible() {
        this.dialogService
            .openDialog({
                title: this.translateService.instant('report.actions.dialogData.sendToCeo.header'),
                message: this.translateService.instant('report.actions.dialogData.sendToCeo'),
                confirmText: this.translateService.instant('report.actions.dialogData.sendToCeo.button')
            })
            .pipe(
                takeUntil(this.destroy$),
                filter(dialogResponse => dialogResponse && dialogResponse.checked),
                tap(async () => {
                    try {
                        await this.mailingService.sendToResponsible(this.report.id);
                        this.snack.openSnackBarSuccess('Success');
                    } catch (error) {
                        this.snack.openSnackBarError('An error has occurred!');
                    }
                })
            )
            .subscribe();
    }
}
